
import { useState } from "react";
import { 
  AlertCircle, 
  CheckCircle,
  Mail
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useEmailTemplates } from "@/hooks/useEmailTemplates";

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ResetPasswordModal = ({ isOpen, onClose }: ResetPasswordModalProps) => {
  const { toast } = useToast();
  const { sendPasswordResetEmail } = useEmailTemplates();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!email || email.trim() === "") {
      setError("Please enter your email address");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Use our custom email template function
      const result = await sendPasswordResetEmail(email.trim());
      
      if (result.success) {
        setSuccess(true);
        toast({
          title: "Success",
          description: "Password reset email sent successfully",
          variant: "default",
        });
      } else if (result.error) {
        throw result.error;
      }
    } catch (error: any) {
      console.error("Password reset error:", error);
      // Provide a more user-friendly error message
      setError(error.message || "Failed to send password reset email. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Reset Password</DialogTitle>
          <DialogDescription>
            Enter your email address and we'll send you a link to reset your password.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 my-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">
                Password reset email sent! Please check your inbox.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <div className="flex items-center">
              <Mail className="mr-2 h-4 w-4 text-gray-500" />
              <label htmlFor="reset-email" className="text-gray-700">Email Address</label>
            </div>
            <Input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              disabled={isLoading || success}
              className="w-full"
            />
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || success}
              className="bg-[#FFC300] hover:bg-[#FFC300]/90 text-gray-900"
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordModal;
